import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword as apiForgotPassword } from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { AuthPageFrame } from '../components/AuthPageFrame.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_ON_LOWEST } from '../styles/forms.ts';

/**
 * Request password reset email. Calls `POST /v1/auth/password/forgot`.
 *
 * The backend intentionally always returns 200 regardless of whether
 * the email exists (no enumeration). This UI mirrors that behavior:
 * the same "check your email" success pane fires on every request; we
 * never distinguish "email found" from "email missing".
 *
 * We still surface PlatformApiError responses that are NOT 200 (e.g.
 * 400 INVALID_PAYLOAD on a totally malformed request). Those indicate
 * a client bug, not an enumeration oracle — and they also trigger a
 * toast so the operator notices even if they scrolled past the inline
 * message.
 */

export function ForgotPassword(): JSX.Element {
  const { fetchImpl } = useSession();
  const toast = useToast();
  const t = useT();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      setError(t('forgot.error.empty_email'));
      return;
    }
    setSubmitting(true);
    try {
      await apiForgotPassword({ email: trimmedEmail }, fetchImpl);
      setSubmittedEmail(trimmedEmail);
      setEmail('');
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      eyebrow={t('forgot.eyebrow')}
      title={t('forgot.subtitle')}
      description={t('forgot.description')}
      maxWidthClassName="max-w-[460px]"
      footer={
        <div className="text-xs text-outline">
          <Link
            to="/login"
            data-testid="forgot-back-login"
            className="rounded-lg hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('common.back_to_login')}
          </Link>
        </div>
      }
    >

        {submittedEmail !== null ? (
          <div
            className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-6"
            data-testid="forgot-success"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
              {t('forgot.success.title')}
            </span>
            <p className="text-sm text-on-surface">
              {t('forgot.success.body', { email: submittedEmail })}
            </p>
            <Link
              to="/login"
              data-testid="forgot-login-link"
              className="mt-2 inline-flex w-fit items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
            >
              {t('common.back_to_login')}
            </Link>
          </div>
        ) : (
          <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('forgot.email')}
              </span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={submitting}
                required
                data-testid="forgot-email"
                className={INPUT_CLASS_ON_LOWEST}
              />
            </label>
            {error !== null ? (
              <p
                className="m-0 text-xs font-medium text-error"
                role="alert"
                data-testid="forgot-error"
              >
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              data-testid="forgot-submit"
              className="mt-2 inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
            >
              {submitting ? t('forgot.submitting') : t('forgot.submit')}
            </button>
          </form>
        )}
    </AuthPageFrame>
  );
}
